/**
 * Apple Wallet wizard — Setup → Credentials → Done, mountable and zero-I/O.
 * Setup is a four-part runbook: the two Apple-portal visits (each a direct
 * deep link, because the Pass Type ID Certificate option sits under
 * `Services`, three screens into a list operators reliably scroll past) and
 * the two `openssl` invocations between them. Those commands are the step's
 * whole reason to exist — Apple hands back a DER `.cer` carrying no private
 * key, while this form's certificate field needs the certificate and its own
 * unencrypted key concatenated in one PEM, and nothing on Apple's side says
 * so. Every step ends by dispatching into `appleWalletSaveIntent$`
 * (./wizard.state.ts), answered by the app-side `.sync` that stores the
 * identity, forwards the credential straight into the EXISTING, unedited
 * saveIntegrationAction$ and writes the outcome back onto this file's own
 * draft.
 *
 * Credentials carries all four values a signed pass rests on — the Pass
 * Type ID and Team ID it is issued under, and the certificate/WWDR pair
 * that signs it. The two identifiers are not credentials (every issued pass
 * publishes both) but every pass task stays inert until both are stored, so
 * splitting them into a step of their own would leave an operator who
 * completed the wizard with nothing delivered.
 *
 * One action button throughout Credentials — "Save & verify" at rest,
 * "Verifying…" and every field locked while a save is in flight. On any
 * failure the wizard stays on this step, fields still populated and
 * editable again — no back-navigation needed to fix a paste. Success
 * advances to Done automatically (the reconciler sets `step: 3` the moment
 * this save's own response reads ok:true), so this view never watches for
 * that transition itself.
 *
 * `hasStoredCredential` — true once a certificate/WWDR pair has already been
 * proven and stored for this workspace — makes either PEM field's blank
 * state mean "keep the stored one", so the Pass Type ID certificate rotates
 * on its own without re-pasting the WWDR intermediate that outlives several
 * of them. When a PEM IS pasted it gates a confirm() step on Save & verify,
 * mirroring lettermint-credentials-form.view.ts's own safeguard: an Apple
 * Wallet certificate carries materially higher blast radius (breaks pass
 * signing) than lettermint's email token, so the same user-control
 * discipline applies here. That confirm names whichever of the two PEMs is
 * actually travelling — certificate, intermediate, or both — since a
 * whole-credential warning over an intermediate-only rotation asserts a
 * replacement of the certificate the operator just chose to keep. It also
 * drives each PEM field's `required` marker, so on a first save the two
 * genuinely-required fields read as required beside the two identifiers.
 * Handed down, with `storedIdentity` the draft seeds from, by the mounting
 * card
 * (web/src/admin/settings/integrations/appleWallet/apple-wallet.view.ts),
 * which alone has access to the live settings cell.
 *
 * Localized via LocalizeMixin, matching every sibling wizard in this set.
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { confirm } from "@mhmo91/schmancy/overlay";
import type { IntegrationSaveFailure } from "@momo/types";
import { html, type TemplateResult } from "lit";
import { property, state } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { when } from "lit/directives/when.js";
import { from, takeUntil } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { organizationId } from "../../workspace/organization.state.ts";
import {
	type AppleWalletPassIdentity,
	type AppleWalletWizardStep,
	appleWalletSaveIntent$,
	appleWalletSaveReady,
	appleWalletWizardDraft,
	openAppleWalletWizard,
} from "./wizard.state.ts";

/** Both deep links land on the exact form rather than the section index:
 *  `add/passTypeId` skips the identifier-type picker, and `certificates/add`
 *  skips a list whose Pass Type ID Certificate option sits under `Services`,
 *  far below the iOS/macOS entries. */
const APPLE_ADD_PASS_TYPE_ID_URL =
	"https://developer.apple.com/account/resources/identifiers/add/passTypeId";
const APPLE_ADD_CERTIFICATE_URL =
	"https://developer.apple.com/account/resources/certificates/add";

/** Generated on the operator's own machine and never transmitted: the key
 *  stays local, only the CSR travels to Apple. `-nodes` is what leaves the
 *  key unencrypted, which is what lets the signer read it back without a
 *  passphrase no field here collects. */
const MAKE_REQUEST_COMMAND = `mkdir -p ~/Desktop/wallet && cd ~/Desktop/wallet
openssl req -new -newkey rsa:2048 -nodes \\
  -keyout pass.key -out pass.certSigningRequest \\
  -subj "/CN=Wallet Pass"`;

/** Apple returns a DER `.cer` holding the certificate alone. The signer needs
 *  it concatenated with the key from the command above (`passcert.pem`) and
 *  the WWDR intermediate it chains to (`wwdr.pem`) — the two values the
 *  Credentials step's two PEM fields take. */
const BUILD_FILES_COMMAND = `cd ~/Desktop/wallet
openssl x509 -inform DER -in ~/Downloads/pass.cer -out pass.crt.pem
cat pass.crt.pem pass.key > passcert.pem
curl -O https://www.apple.com/certificateauthority/AppleWWDRCAG4.cer
openssl x509 -inform DER -in AppleWWDRCAG4.cer -out wwdr.pem`;

// One title per member of the shared verification vocabulary
// (IntegrationVerificationReasonSchema), `satisfies` making a member added to
// it a compile error here rather than a blank heading. Three of them are what
// signing a throwaway pass produces and carry copy written for that outcome;
// the other two are members other registry ids reach and this one cannot —
// `ok` beside a failed outcome is a contradiction, and no precondition gates
// an Apple Wallet save — so both take the generic title, under which the
// provider's own message is what the operator actually reads.
const VERIFICATION_FAILURE_TITLE_KEY = {
	invalid: "admin.integrations.appleWallet.wizard.verify.invalidTitle",
	unreachable: "admin.integrations.appleWallet.wizard.verify.unreachableTitle",
	notAuthorized:
		"admin.integrations.appleWallet.wizard.verify.notAuthorizedTitle",
	ok: "admin.integrations.appleWallet.wizard.verify.unknownTitle",
	notConfigured: "admin.integrations.appleWallet.wizard.verify.unknownTitle",
} as const satisfies Record<
	IntegrationSaveFailure.IntegrationVerificationReason,
	string
>;

const TRANSPORT_FAILURE_COPY_KEY = {
	unauthenticated: {
		title:
			"admin.integrations.appleWallet.wizard.transportErrors.unauthenticated.title",
		body: "admin.integrations.appleWallet.wizard.transportErrors.unauthenticated.body",
	},
	notAuthorized: {
		title:
			"admin.integrations.appleWallet.wizard.transportErrors.notAuthorized.title",
		body: "admin.integrations.appleWallet.wizard.transportErrors.notAuthorized.body",
	},
	invalidInput: {
		title:
			"admin.integrations.appleWallet.wizard.transportErrors.invalidInput.title",
		body: "admin.integrations.appleWallet.wizard.transportErrors.invalidInput.body",
	},
	unknown: {
		title:
			"admin.integrations.appleWallet.wizard.transportErrors.unknown.title",
		body: "admin.integrations.appleWallet.wizard.transportErrors.unknown.body",
	},
} as const;

@schmancyElement("apple-wallet-wizard")
export class AppleWalletWizardView extends LocalizeMixin(SchmancyElement) {
	/** The organization this wizard opened under, read once at construction. */
	private readonly orgId = organizationId.value;

	/** True only once a certificate/WWDR pair has already been proven and
	 *  stored for this workspace — what makes a blank PEM field mean "keep
	 *  the stored one", and what gates the confirm on Save & verify when a
	 *  PEM is pasted. Handed down by the mounting card. */
	@property({ type: Boolean }) hasStoredCredential = false;
	/** The pass identity already stored for this workspace — both fields
	 *  empty before the first configuration. Seeds the draft so a rotation
	 *  retypes neither identifier and cannot mistype one into a value the
	 *  stored certificate was never issued for. Handed down by the mounting
	 *  card. */
	@property({ attribute: false }) storedIdentity: AppleWalletPassIdentity = {
		passTypeId: "",
		teamId: "",
	};

	override connectedCallback(): void {
		super.connectedCallback();
		openAppleWalletWizard(this.storedIdentity, this.hasStoredCredential);
	}

	// Two call sites — the header icon-button and the Done step's Close
	// button.
	private close(): void {
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}

	// Three call sites — schmancy-steps' own back-navigate, the footer's Back
	// button, and the footer's Continue button.
	private goToStep(step: AppleWalletWizardStep): void {
		if (appleWalletWizardDraft.value.saving) return;
		appleWalletWizardDraft.set({ step });
	}

	@state() private confirming = false;
	@state() private copying: string | null = null;
	@state() private copyNotices: Readonly<Record<string, string>> = {};
	@state() private actionHeight: number | null = null;

	private async save(): Promise<void> {
		if (this.confirming) return;
		const draft = appleWalletWizardDraft.value;
		if (draft.saving || !appleWalletSaveReady.get()) return;
		this.actionHeight =
			this.shadowRoot?.firstElementChild?.getBoundingClientRect().height ??
			null;
		const certificatePasted = draft.certificatePem.trim().length > 0;
		const wwdrPasted = draft.wwdrPem.trim().length > 0;
		if (this.hasStoredCredential && (certificatePasted || wwdrPasted)) {
			// A confirm names the artifact actually being replaced: pasting only
			// the intermediate leaves the certificate the operator deliberately
			// kept, so a whole-credential warning would assert a change that
			// isn't happening.
			const replacing =
				certificatePasted && wwdrPasted
					? "both"
					: certificatePasted
						? "certificate"
						: "intermediate";
			this.confirming = true;
			const ok = await confirm({
				title: this.t(
					`admin.integrations.appleWallet.wizard.overwriteConfirm.${replacing}.title`,
				),
				message: this.t(
					`admin.integrations.appleWallet.wizard.overwriteConfirm.${replacing}.message`,
				),
				confirmText: this.t(
					"admin.integrations.appleWallet.wizard.overwriteConfirmButton",
				),
				cancelText: this.t("admin.common.cancel"),
				variant: "danger",
			});
			this.confirming = false;
			if (!ok || !this.isConnected) return;
		}
		// A blank PEM omits its KEY entirely — the save contract reads an
		// absent field as "keep the stored one" and rejects an empty string.
		appleWalletSaveIntent$.next({
			orgId: this.orgId,
			passTypeId: draft.passTypeId,
			teamId: draft.teamId,
			...(certificatePasted && { certificatePem: draft.certificatePem }),
			...(wwdrPasted && { wwdrPem: draft.wwdrPem }),
		});
	}

	// Two call sites — Setup parts 1 and 3, the two Apple-portal visits.
	// `bodyKey` is the catalog KEY, not a rendered fragment: both sites wrap it
	// in the identical typography, so passing markup would hand this helper a
	// caller-shaped hole for no variance at all. Spelled as the literal union
	// rather than `string` because `t()` keys off the catalog's own shape — a
	// widened parameter would take a key no catalog carries.
	private renderPortalStep(
		index: number,
		title: string,
		bodyKey:
			| "admin.integrations.appleWallet.wizard.setup.createId.body"
			| "admin.integrations.appleWallet.wizard.setup.getCertificate.body",
		url: string,
		linkLabel: string,
	): TemplateResult {
		return html`
			<div class="flex gap-3">
				<schmancy-typography type="label" token="md" class="w-5 shrink-0 pt-0.5 text-primary-default">
					${index}
				</schmancy-typography>
				<div class="flex min-w-0 flex-1 flex-col gap-2">
					<schmancy-typography type="title" token="sm">${title}</schmancy-typography>
					<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
						${this.t(bodyKey)}
					</schmancy-typography>
					<a
						href=${url}
						target="_blank"
						rel="noopener"
						class="inline-flex items-center gap-1 self-start text-sm text-primary-default hover:underline"
					>
						<schmancy-icon size="sm">open_in_new</schmancy-icon>
						${linkLabel}
					</a>
				</div>
			</div>
		`;
	}

	// Two call sites — Setup parts 2 and 4, the two terminal commands.
	private renderCommandStep(
		index: number,
		title: string,
		command: string,
	): TemplateResult {
		return html`
			<div class="flex gap-3">
				<schmancy-typography type="label" token="md" class="w-5 shrink-0 pt-0.5 text-primary-default">
					${index}
				</schmancy-typography>
				<div class="flex min-w-0 flex-1 flex-col gap-2">
					<schmancy-typography type="title" token="sm">${title}</schmancy-typography>
					${this.renderCommand(command)}
				</div>
			</div>
		`;
	}

	// Two call sites — the two Setup commands. Every rendered command carries
	// its own copy button, so an operator never hand-selects a multi-line
	// invocation whose one mistyped flag (`-nodes`) yields a credential the
	// signer rejects for a reason no error message here could name.
	private renderCommand(command: string): TemplateResult {
		return html`
			<div class="flex items-start gap-2">
				<schmancy-surface type="containerLowest" class="min-w-0 flex-1 rounded-lg p-3" ${overflowWithin()}>
					<pre class="m-0 font-mono text-xs leading-relaxed whitespace-pre">${command}</pre>
				</schmancy-surface>
				<schmancy-icon-button
					icon="content_copy"
					variant="text"
					size="sm"
					aria-label=${this.t("admin.integrations.appleWallet.wizard.setup.copyAriaLabel")}
                    .busy=${this.copying === command}
                    @click=${() => {
											if (this.copying === command) return;
											this.copying = command;
											from(navigator.clipboard.writeText(command))
												.pipe(takeUntil(this.disconnecting))
												.subscribe({
													next: () => {
														this.copying = null;
														this.copyNotices = {
															...this.copyNotices,
															[command]: this.t(
																"admin.integrations.appleWallet.wizard.setup.copied",
															),
														};
													},
													error: () => {
														this.copying = null;
														this.copyNotices = {
															...this.copyNotices,
															[command]: this.t(
																"admin.integrations.appleWallet.wizard.setup.copyFailed",
															),
														};
													},
												});
										}}
				></schmancy-icon-button>
            </div>
            <schmancy-typography role="status" type="body" token="sm" class="h-10">${this.copyNotices[command] ?? ""}</schmancy-typography>
        `;
	}

	override render(): TemplateResult {
		const draft = appleWalletWizardDraft.value;
		const certificateExpiresAt =
			draft.outcome.kind === "success"
				? draft.outcome.certificateExpiresAt
				: null;
		const certificateExpiresLabel = certificateExpiresAt
			? new Date(certificateExpiresAt).toLocaleDateString(
					this.uiLanguage === "de" ? "de-DE" : "en-GB",
					{ day: "numeric", month: "long", year: "numeric" },
				)
			: null;

		return html`
			<div style=${this.actionHeight === null ? "" : `height:${this.actionHeight}px`} class="flex h-full max-h-[85vh] w-md max-w-full flex-col">
				<div class="flex items-center justify-end px-5 pt-4">
					<schmancy-icon-button
						icon="close"
						variant="text"
						?disabled=${draft.saving}
						aria-label=${this.t("admin.integrations.appleWallet.wizard.closeAriaLabel")}
						@click=${() => this.close()}
					></schmancy-icon-button>
				</div>

				<div ${overflowWithin()} class="flex flex-1 min-h-0 flex-col gap-2 px-5 pb-24">
					<schmancy-steps
						.value=${draft.step}
						@navigate=${(e: CustomEvent<{ value: number }>) => {
							const next = e.detail.value;
							if (next === 1 || next === 2 || next === 3) this.goToStep(next);
						}}
					>
						<schmancy-step
							label=${this.t("admin.integrations.appleWallet.wizard.setup.title")}
							description=${this.t("admin.integrations.appleWallet.wizard.setup.description")}
						>
							<div class="flex flex-col gap-5">
								${this.renderPortalStep(
									1,
									this.t(
										"admin.integrations.appleWallet.wizard.setup.createId.title",
									),
									"admin.integrations.appleWallet.wizard.setup.createId.body",
									APPLE_ADD_PASS_TYPE_ID_URL,
									this.t(
										"admin.integrations.appleWallet.wizard.setup.createId.link",
									),
								)}
								${this.renderCommandStep(
									2,
									this.t(
										"admin.integrations.appleWallet.wizard.setup.makeRequest.title",
									),
									MAKE_REQUEST_COMMAND,
								)}
								${this.renderPortalStep(
									3,
									this.t(
										"admin.integrations.appleWallet.wizard.setup.getCertificate.title",
									),
									"admin.integrations.appleWallet.wizard.setup.getCertificate.body",
									APPLE_ADD_CERTIFICATE_URL,
									this.t(
										"admin.integrations.appleWallet.wizard.setup.getCertificate.link",
									),
								)}
								${this.renderCommandStep(
									4,
									this.t(
										"admin.integrations.appleWallet.wizard.setup.buildFiles.title",
									),
									BUILD_FILES_COMMAND,
								)}
								<schmancy-surface type="containerLow" class="flex flex-col gap-1 rounded-xl p-3">
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
										${this.t("admin.integrations.appleWallet.wizard.setup.expiryNote")}
									</schmancy-typography>
								</schmancy-surface>
							</div>
						</schmancy-step>

						<schmancy-step
							label=${this.t("admin.integrations.appleWallet.wizard.credentials.title")}
							description=${this.t("admin.integrations.appleWallet.wizard.credentials.description")}
						>
							<div class="flex flex-col gap-4">
								<schmancy-input
									label=${this.t("admin.integrations.appleWallet.wizard.credentials.passTypeIdLabel")}
									required
									autocomplete="off"
									size="md"
									.value=${draft.passTypeId}
									?disabled=${draft.saving}
									hint=${this.t("admin.integrations.appleWallet.wizard.credentials.passTypeIdHint")}
									@input=${(e: CustomEvent<{ value: string }>) =>
										appleWalletWizardDraft.set({ passTypeId: e.detail.value })}
								></schmancy-input>
								<schmancy-input
									label=${this.t("admin.integrations.appleWallet.wizard.credentials.teamIdLabel")}
									required
									autocomplete="off"
									size="md"
									.value=${draft.teamId}
									?disabled=${draft.saving}
									hint=${this.t("admin.integrations.appleWallet.wizard.credentials.teamIdHint")}
									@input=${(e: CustomEvent<{ value: string }>) =>
										appleWalletWizardDraft.set({ teamId: e.detail.value })}
								></schmancy-input>
								<schmancy-textarea
									label=${this.t("admin.integrations.appleWallet.wizard.credentials.certificatePemLabel")}
									rows="6"
									?required=${!draft.hasStoredCredential}
									.value=${draft.certificatePem}
									?disabled=${draft.saving}
									hint=${this.t(
										draft.hasStoredCredential
											? "admin.integrations.appleWallet.wizard.credentials.certificatePemKeepHint"
											: "admin.integrations.appleWallet.wizard.credentials.certificatePemHint",
									)}
									@change=${(e: CustomEvent<{ value: string }>) =>
										appleWalletWizardDraft.set({
											certificatePem: e.detail.value,
										})}
								></schmancy-textarea>
								<schmancy-textarea
									label=${this.t("admin.integrations.appleWallet.wizard.credentials.wwdrPemLabel")}
									rows="6"
									?required=${!draft.hasStoredCredential}
									.value=${draft.wwdrPem}
									?disabled=${draft.saving}
									hint=${this.t(
										draft.hasStoredCredential
											? "admin.integrations.appleWallet.wizard.credentials.wwdrPemKeepHint"
											: "admin.integrations.appleWallet.wizard.credentials.wwdrPemHint",
									)}
									@change=${(e: CustomEvent<{ value: string }>) =>
										appleWalletWizardDraft.set({ wwdrPem: e.detail.value })}
								></schmancy-textarea>

								${when(
									!draft.saving && draft.outcome.kind === "verificationFailed",
									() => {
										if (draft.outcome.kind !== "verificationFailed")
											return html``;
										const { reason, detail } = draft.outcome;
										const title = this.t(
											VERIFICATION_FAILURE_TITLE_KEY[reason],
										);
										return html`
										<schmancy-surface type="error" class="flex flex-col gap-2 rounded-xl p-4">
											<div class="flex items-start gap-3">
												<schmancy-icon class="shrink-0 text-error-on-container">error</schmancy-icon>
												<div class="flex flex-col gap-1">
													<schmancy-typography type="title" token="sm" class="text-error-on-container">
														${title}
													</schmancy-typography>
													${when(
														detail.length > 0,
														() => html`
															<schmancy-typography type="body" token="sm" class="text-error-on-container">
																${detail}
															</schmancy-typography>
														`,
													)}
												</div>
											</div>
										</schmancy-surface>
									`;
									},
								)}
								${when(
									!draft.saving && draft.outcome.kind === "transportFailed",
									() => {
										if (draft.outcome.kind !== "transportFailed") return html``;
										const copy =
											TRANSPORT_FAILURE_COPY_KEY[draft.outcome.reason];
										return html`
										<schmancy-surface type="warning" class="flex flex-col gap-2 rounded-xl p-4">
											<div class="flex items-start gap-3">
												<schmancy-icon class="shrink-0 text-warning-on-container">info</schmancy-icon>
												<div class="flex flex-col gap-1">
													<schmancy-typography type="title" token="sm" class="text-warning-on-container">
														${this.t(copy.title)}
													</schmancy-typography>
													<schmancy-typography type="body" token="sm" class="text-warning-on-container">
														${this.t(copy.body)}
													</schmancy-typography>
												</div>
											</div>
										</schmancy-surface>
									`;
									},
								)}
							</div>
						</schmancy-step>

						<schmancy-step
							label=${this.t("admin.integrations.appleWallet.wizard.done.title")}
							description=${this.t("admin.integrations.appleWallet.wizard.done.description")}
						>
							<div class="flex flex-col gap-4">
								<schmancy-typography type="title" token="md">
									${this.t("admin.integrations.appleWallet.wizard.done.heading")}
								</schmancy-typography>
								${when(
									certificateExpiresLabel !== null,
									() => html`
										<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
											${this.t(
												"admin.integrations.appleWallet.wizard.done.certificateExpiresLabel",
												{ date: certificateExpiresLabel ?? "" },
											)}
										</schmancy-typography>
									`,
								)}
							</div>
						</schmancy-step>
					</schmancy-steps>
				</div>

				<schmancy-surface
					opacity="translucent"
					elevated
					class="sticky bottom-0 flex items-center gap-2 px-5 py-4 pt-2"
				>
					${choose(draft.step, [
						[
							1,
							() => html`
								<schmancy-button
									class="ml-auto"
									variant="filled"
									color="primary"
									@click=${() => this.goToStep(2)}
								>
									${this.t("admin.integrations.appleWallet.wizard.continueButton")}
								</schmancy-button>
							`,
						],
						[
							2,
							() => html`
								<schmancy-button
									variant="outlined"
									color="primary"
									?disabled=${draft.saving}
									@click=${() => this.goToStep(1)}
								>
									${this.t("admin.integrations.appleWallet.wizard.backButton")}
								</schmancy-button>
								<schmancy-button
									class="ml-auto"
									variant="filled"
									color="primary"
									.busy=${draft.saving || this.confirming}
                                    ?disabled=${!appleWalletSaveReady.get() && !draft.saving}
									@click=${() => this.save()}
								>
                                    ${this.t("admin.integrations.appleWallet.wizard.saveButton")}
								</schmancy-button>
							`,
						],
						[
							3,
							() => html`
								<schmancy-button class="ml-auto" variant="filled" color="primary" @click=${() => this.close()}>
									${this.t("admin.integrations.appleWallet.wizard.closeButton")}
								</schmancy-button>
							`,
						],
					])}
				</schmancy-surface>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"apple-wallet-wizard": AppleWalletWizardView;
	}
}
